<template>
    <div :class="[isValid ? 'valid' : '']">
        <vue-select
            v-model="selectedCountry"
            label="value"
            :options="countryList"
            :placeholder="placeholder"
            :components="{ OpenIndicator }"
            :class="[selectedCountry ? '' : 'not-selected']"
            :input-id="inputId"
            :disabled="countryDisable"
            @open="toggleLabelVisibility"
            @close="toggleLabelVisibility"
            @keydown="filterInput"
        >
            <template #selected-option="{ value, key }">
                <span
                    :class="`country-flag fi fi-${String(key || '').toLowerCase()}`"
                />
                <span>{{ value }}</span>
            </template>

            <template #option="{ value, key }">
                <span
                    :class="`country-flag fi fi-${String(key || '').toLowerCase()}`"
                />
                <span>{{ value }}</span>
            </template>

            <template #no-options>
                {{ $t(`${TRANSLATION_KEY}.invalidCountry`) }}
            </template>
        </vue-select>
        <span
            class="custom-select__label"
            :style="{ opacity: isLabelVisible ? 1 : isValid ? 1 : 0 }"
        >
            {{ label }}
        </span>

        <Error :name="name" />
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    computed,
    watch,
    onMounted,
    h,
    PropType,
} from 'vue';
import VueSelect from 'vue-select';
import Error from '@/components/basic/Error.vue';

type CountryOption = {
    key: string | number;
    value: string;
};

export default defineComponent({
    name: 'TheCountrySelect',
    components: {
        VueSelect,
        Error,
    },
    props: {
        name: {
            type: String,
            default: '',
        },
        countryList: {
            type: Array as PropType<CountryOption[]>,
            default: (): CountryOption[] => [],
        },
        placeholder: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        inputId: {
            type: String,
            default: 'countrySelect',
        },
        defaultValue: {
            type: String,
            default: '',
        },
        countryDisable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['selected-country'],
    setup(props, { emit }) {
        const selectedCountry = ref<CountryOption | null>(null);
        const isLabelVisible = ref(false);
        const TRANSLATION_KEY = 'basic.fields.TheCountrySelect';

        const OpenIndicator = {
            render() {
                return h('span', { class: 'custom-select__input-arrow' });
            },
        };

        const toggleLabelVisibility = () => {
            isLabelVisible.value = !isLabelVisible.value;
        };

        const setDefaultCountry = () => {
            if (props.defaultValue) {
                selectedCountry.value =
                    props.countryList.find(
                        (country) => String(country.key) === props.defaultValue
                    ) || null;
            }
        };

        watch(selectedCountry, (newValue) => {
            if (newValue) {
                emit('selected-country', String(newValue.key));
            }
        });

        watch(
            () => props.defaultValue,
            () => {
                setDefaultCountry();
            },
            { immediate: true }
        );

        onMounted(() => {
            setDefaultCountry();
        });

        const filterInput = (e: KeyboardEvent) => {
            if (!/^[\p{L}]+$/u.test(e.key)) {
                e.preventDefault();
            }
        };

        const isValid = computed(() => selectedCountry.value !== null);

        return {
            selectedCountry,
            isLabelVisible,
            TRANSLATION_KEY,
            OpenIndicator,
            toggleLabelVisibility,
            filterInput,
            isValid,
        };
    },
});
</script>

<style>
.vs--disabled .vs__dropdown-toggle {
    border-radius: 10px;
}
</style>
